import { Button, Pane, Tooltip } from 'components/common';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth, useGet } from 'hooks';
import DataEnrichmentTable from './Tables/DataEnrichmentTable';
import {
  dataEnrichmentTable,
  getCredits,
  getDataEnrichment,
} from 'services/api';
import { ActionsHeader } from './styled';
import {
  getFilters,
  getSignalRDataEnrichmentId,
} from 'selectors/dataEnrichment';
import { setFilters } from 'actions/dataEnrichment';
import DataEnrichmentFilters from './Filters';
import styled from 'styled-components';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { ENRICHMENT_TYPE } from './helpers';
import Wizard from './Wizards';
import { isFullTrial } from 'utils/helpers/license';
import { BetaWord } from 'components/common/BetaTag/styled';
import { formatNumber, siNumber } from 'utils/number';
import { notification } from 'antd';
import { UpgradeLicense } from 'utils/constants';

const Title = styled.div`
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaneRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 40px;
`;

const PaneColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  max-width: 280px;
`;

const PaneText = styled.div`
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const SubText = styled.div`
  font-size: 10px;
`;

const ActionPane = styled(Pane)`
  padding: 15px;
  margin-bottom: 16px;
`;

const ActionPaneContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const modals = {
  upload: Wizard,
};

const EnrichmentHistory = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const filters = useSelector(getFilters(user.userId));
  const [localData, setLocalData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalOptions, setModalOptions] = useState({});
  const { push } = useHistory();
  const dataEnrichmentId = useSelector(getSignalRDataEnrichmentId);
  const location = useLocation();

  const handleViewReport = useCallback((id, isProcessing = false, type) => {
    setModalOptions({ summaryId: id, isProcessing, type });
    setModal('upload');
  }, []);

  const [{ res: credits }, fetchCredits] = useGet({
    url: getCredits,
  });

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const id = params.get('summaryId');
      handleViewReport(id);
    }
  }, [location.search, handleViewReport]);

  const [{ res }, fetchDataEnrichment] = useGet({
    url: getDataEnrichment(dataEnrichmentId),
    onMount: false,
  });

  useEffect(() => {
    if (dataEnrichmentId) {
      fetchDataEnrichment();
      fetchCredits();
    }
  }, [dataEnrichmentId, fetchDataEnrichment, fetchCredits]);

  useEffect(() => {
    if (res) {
      setLocalData(data => {
        if (data?.data) {
          const updatedData = data.data.map(item =>
            item.id === res.id ? { ...res } : item
          );
          return { ...data, data: updatedData };
        }
        return data;
      });
    }
  }, [res, dispatch]);

  const [
    { res: allData, loading: isAllLoading },
    getAllEnrichmentData,
  ] = useGet({
    url: dataEnrichmentTable,
    onMount: false,
  });

  useEffect(() => {
    if (user.isUserManager && !filters?.userIds?.length) {
      dispatch(setFilters(user.userId, { ...filters, userIds: [user.userId] }));
    }
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAllLoading) {
      setLocalData(allData);
    }
  }, [allData, isAllLoading]);

  function updateFilters(filters) {
    dispatch(setFilters(user.userId, filters));
  }

  useEffect(() => {
    if (!isInitialLoad) {
      let searchFilters = { ...filters };
      if (!user.isUserManager) {
        searchFilters.userIds = [];
      }
      getAllEnrichmentData({ query: searchFilters });
      fetchCredits();
    }
  }, [
    filters,
    user.isUserManager,
    isInitialLoad,
    getAllEnrichmentData,
    fetchCredits,
  ]);

  function handleViewList(id, type) {
    if (type === ENRICHMENT_TYPE.CONTACT || type === ENRICHMENT_TYPE.EMAIL) {
      push(`/lists/researcher/${id}`);
    } else if (type === ENRICHMENT_TYPE.ORGANISATION) {
      push(`/lists/organisation/${id}`);
    }
  }

  const Modal = modals[modal];

  const handleCloseModal = options => {
    setModal(null);
    setModalOptions({});
    if (options?.reload) {
      dispatch(setFilters(user.userId, { ...filters }));
    }
  };

  const uploadYourFile = ({ type }) => {
    if (credits?.remainingCredits > 0) {
      setModal('upload');
      setModalOptions({ type });
    } else {
      notification.open({
        message: 'Data Enrichment Limit Reached',
        description: `You have already reached your ${formatNumber(
          credits?.credits || 10000
        )} enrichment limit for this month`,
        placement: 'bottomLeft',
        duration: 5,
      });
    }
  };

  return (
    <Pane>
      <ActionPaneContainer>
        <ActionPane>
          <PaneRow>
            <PaneColumn>
              <img src={'EnrichEmails.svg'} alt='Enrich Emails' />
            </PaneColumn>
            <PaneColumn>
              <Title>
                Enrich your Emails <BetaWord>Beta</BetaWord>
              </Title>
              <PaneText>
                Upload a list of emails and we will assist you in finding their
                SciLeads profiles
                <SubText>
                  {`Shared ${siNumber(
                    credits?.credits || 10000,
                    0
                  )}/month limit`}
                </SubText>
              </PaneText>
              <Tooltip
                title={
                  isFullTrial(user) ? (
                    'Data Enrichment not available on a trial. If you would like access to this feature, please contact your Account Manager to purchase a licence.'
                  ) : user.isPartnerAccount ? (
                    <UpgradeLicense />
                  ) : (
                    ''
                  )
                }
              >
                <div>
                  <Button
                    onClick={() => uploadYourFile({ type: 'email' })}
                    type='primary'
                    disabled={
                      isFullTrial(user) ||
                      !credits?.credits ||
                      user.isPartnerAccount
                    }
                  >
                    Upload Your File
                  </Button>
                </div>
              </Tooltip>
            </PaneColumn>
          </PaneRow>
        </ActionPane>
        <ActionPane>
          <PaneRow>
            <PaneColumn>
              <img src={'EnrichOrg.svg'} alt='Enrich Organisation' />
            </PaneColumn>
            <PaneColumn>
              <Title>
                Enrich your Organisations <BetaWord>Beta</BetaWord>
              </Title>
              <PaneText>
                Upload a list of organisations and we will assist you in finding
                their SciLeads profiles
                <SubText>
                  {`Shared ${siNumber(
                    credits?.credits || 10000,
                    0
                  )}/month limit`}
                </SubText>
              </PaneText>
              <Tooltip
                title={
                  isFullTrial(user) ? (
                    'Data Enrichment not available on a trial. If you would like access to this feature, please contact your Account Manager to purchase a licence.'
                  ) : user.isPartnerAccount ? (
                    <UpgradeLicense />
                  ) : (
                    ''
                  )
                }
              >
                <div>
                  <Button
                    disabled={
                      isFullTrial(user) || !credits || user.isPartnerAccount
                    }
                    type='primary'
                    onClick={() => uploadYourFile({ type: 'organisation' })}
                  >
                    Upload Your File
                  </Button>
                </div>
              </Tooltip>
            </PaneColumn>
          </PaneRow>
        </ActionPane>
      </ActionPaneContainer>
      <ActionsHeader>
        <DataEnrichmentFilters
          credits={credits}
          filters={filters}
          user={user}
          updateFilters={updateFilters}
        />
      </ActionsHeader>
      {modal && <Modal onClose={handleCloseModal} {...modalOptions} />}
      <DataEnrichmentTable
        user={user}
        isLoading={isAllLoading}
        data={localData}
        updateFilters={updateFilters}
        filters={filters}
        handleViewReport={handleViewReport}
        handleViewList={handleViewList}
      />
    </Pane>
  );
};

export default EnrichmentHistory;
